import projectValue from '#calculator/projectValue'
import { FilingStatus } from '#dictionary/filingStatuses'

import type { StandardDeductionGroup } from '.'
import { STANDARD_DEDUCTIONS, STANDARD_DEDUCTIONS_GROWTH_RATE } from '.'
import {
  getStandardDeductionGroup,
  type StandardDeductionProfile,
} from './getStandardDeductionGroup'

type Year = number

// Source index: filingStatus → year → group → value. Built once at module
// load; source data is static so no invalidation is ever needed.
const sourceIndex = buildSourceIndex()

const lastSourceYear = Math.max(...STANDARD_DEDUCTIONS.map(d => d.year))

// Returns the standard-deduction dollar amount for a given household in a
// given (filingStatus, year). The household's group tier is computed
// internally from `profile` — callers pass the profile, not a raw group.
//
// Falls through to 0 when the combination has no source row and can't be
// projected. Matches the tax domain: no matching deduction row means no
// deduction applies.
//
// - year ≤ lastSourceYear → returns the source row's value directly.
// - year > lastSourceYear → projects from the last source year using
//   compound growth at STANDARD_DEDUCTIONS_GROWTH_RATE.
// - Missing (filingStatus × group) row (e.g. Single g4) or year before
//   source begins → 0.
export const getStandardDeduction = (
  filingStatus: FilingStatus,
  year: Year,
  profile: StandardDeductionProfile
): number => {
  const group = getStandardDeductionGroup(profile)

  const sourceValue = sourceIndex.get(filingStatus)?.get(year)?.get(group)
  if (sourceValue !== undefined) return sourceValue

  if (year <= lastSourceYear) return 0

  const baseValue = sourceIndex
    .get(filingStatus)
    ?.get(lastSourceYear)
    ?.get(group)
  if (baseValue === undefined) return 0

  return projectValue(
    baseValue,
    STANDARD_DEDUCTIONS_GROWTH_RATE,
    lastSourceYear,
    year
  )
}

type SourceIndex = ReadonlyMap<
  FilingStatus,
  ReadonlyMap<Year, ReadonlyMap<StandardDeductionGroup, number>>
>

function buildSourceIndex(): SourceIndex {
  const index = new Map<
    FilingStatus,
    Map<Year, Map<StandardDeductionGroup, number>>
  >()
  STANDARD_DEDUCTIONS.forEach(deduction => {
    const yearMap = getOrCreate(index, deduction.filingStatus, () => new Map())
    const groupMap = getOrCreate(yearMap, deduction.year, () => new Map())
    groupMap.set(deduction.standardDeductionGroup, deduction.value)
  })
  return index
}

function getOrCreate<K, V>(map: Map<K, V>, key: K, create: () => V): V {
  if (!map.has(key)) map.set(key, create())
  return map.get(key)!
}
